(ns io.daskul.tutorial.webapp.components.guardian-panel
  "The student's Guardian tab (#974) — who pays, who else is linked, and which
   discounts reach this student.

   Registered as a :custom-child-panels component for [:student
   :student-guardian], so it replaces the generic child table entirely and the
   tab stops being a doorway to the guardian detail page.

   The payer is assumed to be a guardian. A school can declare it bills
   students directly (`supported-customer-types`, resolved in
   domain/billing/payer.clj), but that setting is resolved server-side and never
   reaches the client — only its effect, as fields the server did or did not put
   on the student form. A self-paying student therefore has no link row here and
   reads as 'no payer'.

   Three reads, whatever the size of the family: this student's links, the
   payer's links (for the siblings), and the payer's discounts — the last one
   also supplying every sibling's count. See guardian-panel-logic."
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [clojure.string :as str]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.notifications :as notif]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.form.field :refer [form-field]]
   [io.daskul.webapp.widgets.form.lookup-create :refer [lookup-inline-create]]
   [io.daskul.webapp.widgets.form.lookup-edit :refer [lookup-inline-edit]]
   [io.daskul.webapp.widgets.layout.slide-over :refer [slide-over]]
   [io.daskul.webapp.views.aggregate :refer [child-aggregate-panel]]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.tutorial.webapp.components.guardian-panel-logic :as logic]))

(defn- initials [full-name]
  (->> (str/split (or full-name "") #"\s+")
       (remove str/blank?)
       (take 2)
       (map (comp str/upper-case first))
       (str/join)))

(defn- money
  "A discount's value in its own unit — percentage discounts are not money."
  [{:keys [value value-type]}]
  (if (= "percentage" value-type)
    (str value "%")
    (format/format-currency value)))

(defn- humanize [s]
  (when (seq (str s))
    (str/capitalize (str/replace (str s) "-" " "))))

(defui ^:private avatar
  [{:keys [label size] :or {size 42}}]
  ($ :div {:style {:width size :height size :borderRadius "50%" :flex "none"
                   :display "grid" :placeItems "center"
                   :background "var(--action-soft-bg)" :color "var(--action-soft-fg)"
                   :fontWeight 600 :fontSize (if (< size 36) 11 15)}}
     label))

(defui ^:private payer-card
  "With no payer this renders as the empty state — and the cards below it still
   render too. Collapsing the whole tab to one line was a dead end: the
   guardians are still linked, but nothing showed them and nothing offered a way
   to set the flag back."
  [{:keys [payer siblings on-edit on-edit-guardian candidates on-make-payer]}]
  (let [name (logic/guardian-name payer)
        person (get-in payer [:guardian :person])]
    ($ ui/box {:pb 28 :style {:borderBottom "1px solid var(--border)"}}
      (if (nil? payer)
        ($ ui/stack {:gap "xs"}
          ($ ui/title {:order 4} "Primary payer")
          ($ ui/text {:size "sm" :c "dimmed"}
             "No payer set yet — add a guardian to bill this student.")
          ;; Naming the fix is not offering it. Every linked guardian gets a
          ;; button here, so setting a payer is one click from the card that
          ;; says it is missing — rather than a hunt for the row's pencil.
          (if (seq candidates)
            ($ ui/stack {:gap 4 :mt "xs"}
              (for [link candidates]
                ($ ui/group {:key (str (:id link)) :gap "sm" :wrap "nowrap"}
                  ($ ui/button {:size "xs"
                                :variant "light"
                                :onClick #(on-make-payer link)}
                     (str "Make " (or (get-in link [:guardian :person :first-name])
                                      (logic/guardian-name link))
                          " the payer"))
                  ($ ui/text {:size "xs" :c "dimmed"} (humanize (:relationship link))))))
            ($ ui/text {:size "xs" :c "dimmed"}
               "Link a guardian below first — a discount is billed to the payer.")))
      ($ ui/stack {:gap "sm"}
        ($ ui/group {:justify "space-between" :align "center"}
          ($ ui/title {:order 4} "Primary payer")
          ;; Opens the linked-guardians form for this row — the payer is hidden
          ;; from that table, so this is its way in.
          ($ ui/button {:size "xs"
                        :variant "light"
                        :leftSection ($ icons/icon-edit {:size 14})
                        :onClick on-edit}
             "Edit"))
        ($ ui/group {:gap "md" :wrap "nowrap" :align "center"}
          ($ avatar {:label (initials name)})
          ($ ui/stack {:gap 2}
            ;; The NAME edits the person; the Edit button edits the link. Two
            ;; records, so two actions — and the link form keeps Unlink, which
            ;; would read as "delete this guardian" if the same form also
            ;; carried their phone and email.
            ($ ui/anchor {:fw 600 :onClick on-edit-guardian
                          :style {:cursor "pointer"}}
               name)
            ($ ui/text {:size "sm" :c "dimmed"} (humanize (:relationship payer)))
            ($ ui/group {:gap "xs"}
              ($ ui/badge {:variant "light" :size "sm"} "Primary payer")
              (when (:primary-contact payer)
                ($ ui/badge {:variant "default" :size "sm"} "Primary contact")))))
        ($ ui/group {:gap "xl"}
          (when-let [phone (:phone person)]
            ($ ui/stack {:gap 0}
              ($ ui/text {:size "xs" :tt "uppercase" :fw 600 :c "dimmed"} "Phone")
              ($ ui/text {:size "sm"} phone)))
          (when-let [email (:email person)]
            ($ ui/stack {:gap 0}
              ($ ui/text {:size "xs" :tt "uppercase" :fw 600 :c "dimmed"} "Email")
              ($ ui/text {:size "sm"} email))))

        ;; The payer's other students. A count, not just a name: entering
        ;; sibling discounts one at a time is only workable if the page can
        ;; answer "did I already do this one?". Absence is stated rather than
        ;; left blank — a missing block reads as "not loaded", not "none".
        ;; A sunken block, not a divider. Once the sections are separated by
        ;; whitespace alone, a rule INSIDE one reads as more separation than the
        ;; No ground and no rule of its own: the SECTIONS carry the rules, so
        ;; anything without one is legibly subordinate.
        ($ ui/box {:mt "md"}
          ($ ui/text {:size "xs" :tt "uppercase" :fw 600 :c "dimmed" :mb 4}
             "Also pays for")
          (if (empty? siblings)
            ($ ui/text {:size "sm" :c "dimmed"} "No other students under this payer.")
            ($ ui/stack {:gap 0}
              ;; Name then count, together. The relationship is dropped: every
              ;; row shares one payer, so every row said the same word, and a
              ;; column that never varies is not information. Nor is the row
              ;; justified edge to edge any more — a count the eye cannot pair
              ;; with its name stops meaning anything.
              (for [{:keys [student-id first-name discount-count]} siblings]
                ($ ui/group {:key (str student-id) :gap "sm" :wrap "nowrap"
                             :style {:padding "6px 0"}}
                  ($ :<>
                    ($ avatar {:label (initials first-name) :size 28})
                    ($ ui/anchor {:href (router/href :aggregate/detail
                                                     {:agg "students" :id student-id})
                                  :fw 600 :size "sm"}
                       first-name))
                  ($ ui/text {:size "sm" :c "dimmed"}
                     (if (zero? discount-count)
                       "No discounts"
                       (str discount-count (if (= 1 discount-count)
                                             " discount" " discounts"))))))))))))))

(defui ^:private linked-guardians-card
  "Link / edit / unlink, delegated to the framework's child panel rather than
   rebuilt here — it already owns the junction add ('Link a guardian', which
   can create one on the way), the row form for relationship and the primary
   flags, and delete. Replacing the whole tab with a custom panel took those
   away; this hands them back.

   It lists every link, the payer included, so there is one place to manage
   them and no card above that merely repeats the table without its actions."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field junction add
           empty-message parent-record payer-name on-changed
           exclude-ids initial-edit-id on-slide-over-change]}]
  ($ ui/box {:mt 32 :pb 28 :style {:borderBottom "1px solid var(--border)"}}
    ($ ui/stack {:gap "sm"}
      ($ child-aggregate-panel
        {:parent-agg-key parent-agg-key
         :parent-id parent-id
         :child-agg-key child-agg-key
         :fk-field fk-field
         :junction junction
         :add add
         :empty-message empty-message
         :parent-record parent-record
         :title "Linked guardians"
         ;; NB :refresh-parent is a flag the framework reads, not a callback it
         ;; calls — passing a function here only ever meant "truthy". The panel
         ;; re-reads through :on-slide-over-change below instead.
         :refresh-parent true
         ;; The payer has its own section above; a row here would be a
         ;; duplicate of it, without being any more editable.
         :exclude-ids exclude-ids
         :initial-edit-id initial-edit-id
         :on-slide-over-change on-slide-over-change})
      ;; Two lists of people on one tab: without this, a discount added below
      ;; reads as if it might bill whichever guardian was last looked at.
      (when payer-name
        ($ ui/text {:size "xs" :c "dimmed"}
           (str "Only the payer is billed. A guardian here is someone to reach, not someone "
                "to charge — a discount added below always goes to " payer-name "."))))))

(defui ^:private add-discount
  "The generic discount create form, pre-scoped: the payer and this student are
   supplied rather than asked for, which is the whole reason this entry point
   exists. `lookup-inline-create` already does schema-fetch → render → POST."
  [{:keys [open? on-close on-created payer-guardian-id student-id payer-name student-name]}]
  ($ slide-over {:open? open? :on-close on-close :title "Add Discount" :size "lg"}
    (when open?
      ($ ui/stack {:gap "sm"}
        ($ ui/text {:size "sm" :c "dimmed"}
           (str "Billed to " payer-name " · for " student-name))
        ($ lookup-inline-create
          {:resource "discounts"
           :label "Discount"
           :initial-data {:customer-id payer-guardian-id :beneficiary-id student-id}
           :hidden-fields #{:customer-id :beneficiary-id}
           :on-created (fn [_] (on-close) (on-created))
           :on-cancel on-close
           :form-field-component form-field})))))

;; api/request! writes outside the re-frame flow, so a confirmation hops through
;; an event to reach the :notification effect (same shape invoice-lines uses).
(rf/reg-event-fx ::notify
  (fn [_ [_ notification]] {:notification notification}))

(defn- notify! [n] (rf/dispatch [::notify n]))

(defn- transition!
  "Retire or revive a discount. A pricing rule is deactivated, not deleted —
   past invoices were priced by it."
  [id event on-done]
  (api/request! {:method :post
                 :path (str "/discounts/" id "/transition")
                 :params {:event event}
                 :on-success (fn [_]
                               (notify! (notif/success
                                         (if (= event "deactivate") "Deactivated" "Activated")
                                         (if (= event "deactivate")
                                           "This discount no longer applies to new invoices."
                                           "This discount applies again to new invoices.")))
                               (on-done))
                 :on-error (fn [_]
                             (notify! (notif/error "Could not change the discount"
                                                   "Nothing was changed — try again."))
                             (on-done))}))


(defui ^:private discounts-card
  [{:keys [discounts payer-name student-name payer-guardian-id student-id on-created]}]
  (let [[adding? set-adding!] (uix/use-state false)
        ;; The discount open in the edit slide-over, or nil.
        [editing set-editing!] (uix/use-state nil)
        ;; The discount awaiting a state change, or nil. It alters what the next
        ;; invoice charges, so it is a deliberate act.
        [confirming set-confirming!] (uix/use-state nil)
        [working? set-working!] (uix/use-state false)
        confirm-active? (= "active" (:state confirming))]
    ;; Last section: no rule. The card's own edge closes it, and a rule there
    ;; would be a line with nothing under it.
    ($ ui/box {:mt 32}
      ($ ui/stack {:gap "sm"}
        ($ ui/group {:justify "space-between" :align "center"}
          ($ ui/title {:order 4} "Discounts")
          ($ ui/button {:size "xs"
                        :variant "light"
                        :leftSection ($ icons/icon-plus {:size 16})
                        :disabled (nil? payer-guardian-id)
                        :onClick #(set-adding! true)}
             "Add Discount"))

        ($ add-discount {:open? adding?
                         :on-close #(set-adding! false)
                         :on-created on-created
                         :payer-guardian-id payer-guardian-id
                         :student-id student-id
                         :payer-name payer-name
                         :student-name student-name})

        (if (empty? discounts)
          ($ ui/text {:size "sm" :c "dimmed"}
             (if payer-guardian-id
               "No discounts yet."
               "No discounts. A discount is billed to the payer, so set one first."))
          ($ ui/table {}
            ($ ui/thead
              ($ ui/tr
                ($ ui/th "Type")
                ($ ui/th "Package")
                ($ ui/th "Value")
                ($ ui/th "Applies to")
                ($ ui/th "Valid")
                ($ ui/th "Status")
                ($ ui/th "")))
            ($ ui/tbody
              (for [d discounts]
                (let [active? (= "active" (:state d))]
                  ($ ui/tr {:key (str (:id d))}
                    ($ ui/td (humanize (:discount-type d)))
                    ($ ui/td (or (get-in d [:package :name]) "—"))
                    ($ ui/td {:style {:fontVariantNumeric "tabular-nums" :fontWeight 600}}
                       (money d))
                    ($ ui/td ($ ui/text {:size "xs" :c "dimmed"}
                                (logic/applies-to-label d {:payer-name payer-name
                                                           :student-name student-name})))
                    ;; One cell, because the pair is one fact: when this applies.
                    ;; "— → —" is a discount with no bound at all.
                    ($ ui/td ($ ui/text {:size "xs" :c "dimmed"
                                         :style {:whiteSpace "nowrap"}}
                                ;; No bounds at all is a single "—": "— → —" is
                                ;; three glyphs saying nothing, and it crowded
                                ;; the columns that do speak.
                                (if (or (:valid-from d) (:valid-until d))
                                  (str (or (:valid-from d) "—") " → " (or (:valid-until d) "—"))
                                  "—")))
                    ;; Mantine's Badge clips its own label, so a squeezed column
                    ;; renders "ACTI…" — a status you cannot read is worse than
                    ;; no column. Let the label size itself.
                    ($ ui/td {:style {:whiteSpace "nowrap"}}
                      ($ ui/badge {:variant "light" :size "sm"
                                   :styles #js {:label #js {:overflow "visible"}}}
                         (:state d)))
                    ($ ui/td
                      ($ ui/group {:gap 4 :wrap "nowrap" :justify "flex-end"}
                        ($ ui/button {:size "compact-xs"
                                      :variant "subtle"
                                      :color (if active? "gray" "green")
                                      :onClick #(set-confirming! d)}
                           (if active? "Deactivate" "Activate"))
                        ($ ui/action-icon {:variant "subtle" :color "gray"
                                           :aria-label "Edit discount"
                                           :onClick #(set-editing! d)}
                           ($ icons/icon-edit {:size 16}))))))))))

        ($ slide-over {:open? (some? editing)
                       :on-close #(set-editing! nil)
                       :title "Edit Discount"
                       :size "lg"}
          (when editing
            ($ ui/stack {:gap "sm"}
              ($ ui/text {:size "sm" :c "dimmed"}
                 (str "Billed to " payer-name " · "
                      (logic/applies-to-label editing {:payer-name payer-name
                                                       :student-name student-name})))
              ($ lookup-inline-edit
                {:resource "discounts"
                 :record editing
                 ;; Who it bills and who it is for are the reason this row is on
                 ;; this page; changing them here would move the discount to
                 ;; someone else's page without saying so.
                 :hidden-fields #{:customer-id :beneficiary-id}
                 :on-saved (fn [] (set-editing! nil) (on-created))
                 :on-cancel #(set-editing! nil)
                 :form-field-component form-field}))))

        ($ ui/modal {:opened (some? confirming)
                     :onClose #(set-confirming! nil)
                     :title (if confirm-active? "Deactivate discount?" "Activate discount?")
                     :centered true
                     :size "sm"}
          ($ ui/stack {:gap "md"}
            ($ ui/text {:size "sm"}
               (if confirm-active?
                 (str "This discount stops applying to new invoices. Invoices already "
                      "issued keep the price they were given.")
                 (str "This discount starts applying again to new invoices. Invoices "
                      "already issued are unaffected.")))
            ($ ui/group {:justify "flex-end" :gap "sm"}
              ($ ui/button {:variant "subtle" :color "gray" :size "sm"
                            :onClick #(set-confirming! nil)}
                 "Cancel")
              ($ ui/button {:size "sm"
                            :loading working?
                            :onClick (fn []
                                       (set-working! true)
                                       (transition! (:id confirming)
                                                    (if confirm-active? "deactivate" "activate")
                                                    (fn []
                                                      (set-working! false)
                                                      (set-confirming! nil)
                                                      (on-created))))}
                 (if confirm-active? "Deactivate" "Activate")))))))))

(defui guardian-panel
  "Props come from the detail view's custom-child-panel slot: :parent-id is the
   student, :parent-record the student row."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field junction add
           empty-message parent-record]}]
  (let [[links set-links!] (uix/use-state nil)
        ;; Which link the shared form is editing — the payer card's Edit and
        ;; the table's own pencil both write it.
        [editing-link set-editing-link!] (uix/use-state nil)
        ;; The guardian whose OWN fields are open (name, phone, email), or nil.
        ;; A different record from the link above.
        [editing-guardian set-editing-guardian!] (uix/use-state nil)
        [payer-links set-payer-links!] (uix/use-state [])
        [discounts set-discounts!] (uix/use-state [])
        {:keys [payer others]} (logic/split-guardians (or links []))
        payer-guardian-id (:guardian-id payer)
        ;; Two readers, one truth. This panel reads the links itself; the
        ;; framework's child table and its form read re-frame's copy. Refresh
        ;; only one and the card says "Primary payer" while the form it opens
        ;; shows the toggle off — the same rows, disagreeing. So re-read both.
        fetch-links! (uix/use-callback
                      (fn []
                        (when parent-id
                          (rf/dispatch [::agg.events/list-children
                                        parent-agg-key parent-id child-agg-key fk-field])
                          (api/request! {:method :get
                                         :path "/student-guardians"
                                         :params {:student-id parent-id}
                                         :on-success #(set-links! (or (:items %) []))
                                         :on-error (fn [_] (set-links! []))})))
                      [parent-id parent-agg-key child-agg-key fk-field])
        refresh-links! fetch-links!
        fetch-discounts! (uix/use-callback
                          (fn []
                            (when payer-guardian-id
                              (api/request! {:method :get
                                             :path "/discounts"
                                             :params {:customer-id payer-guardian-id}
                                             :on-success #(set-discounts! (or (:items %) []))
                                             :on-error (fn [_] (set-discounts! []))})))
                          [payer-guardian-id])
        refresh-discounts! fetch-discounts!
        student-name (or (get-in parent-record [:person :first-name])
                         (:first-name parent-record))]

    (uix/use-effect
     (fn [] (fetch-links!) js/undefined)
     [fetch-links!])

    ;; The payer's own rows give the siblings; the payer's discounts give both
    ;; this student's list and every sibling's count.
    (uix/use-effect
     (fn []
       (when payer-guardian-id
         (fetch-discounts!))
       js/undefined)
     [payer-guardian-id fetch-discounts!])

    (uix/use-effect
     (fn []
       (when payer-guardian-id
         (api/request! {:method :get
                        :path "/student-guardians"
                        :params {:guardian-id payer-guardian-id}
                        :on-success #(set-payer-links! (or (:items %) []))
                        :on-error (fn [_] (set-payer-links! []))}))
       js/undefined)
     [payer-guardian-id])

    (cond
      (nil? links)
      ($ ui/card {:shadow "sm" :padding "lg" :radius "md" :withBorder true}
        ($ ui/loader {:size "sm"}))

      :else
      (let [payer-name (get-in payer [:guardian :person :first-name])]
        ;; ONE surface. The sections are divided by their headers, not by three
        ;; shadowed cards — the tab is a single subject (who pays, who else is
        ;; linked, what they are charged), and boxing each part made it read as
        ;; three unrelated widgets that happen to share a page.
        ($ ui/card {:shadow "sm" :padding "lg" :radius "md" :withBorder true}
          ($ payer-card {:payer payer
                         :siblings (logic/siblings payer-links discounts parent-id)
                         :on-edit #(set-editing-link! (str (:id payer)))
                         :on-edit-guardian
                         #(set-editing-guardian!
                           (assoc (get-in payer [:guardian :person])
                                  :id (:guardian-id payer)))
                         :candidates others
                         :on-make-payer
                         (fn [link]
                           (api/request!
                            {:method :patch
                             :path (str "/student-guardians/" (:id link))
                             :params {:primary-payer true}
                             :on-success
                             (fn [_]
                               ;; Nothing has been billed — the flag names who
                               ;; will be. Say that, in the words the badge and
                               ;; the column already use.
                               (notify! (notif/success
                                         "Primary payer set"
                                         (str (logic/guardian-name link)
                                              " is now the primary payer.")))
                               (refresh-links!))
                             :on-error
                             (fn [_]
                               (notify! (notif/error "Could not set the payer"
                                                     "Nothing was changed — try again."))
                               (refresh-links!))}))})
          ($ slide-over {:open? (some? editing-guardian)
                         :on-close #(set-editing-guardian! nil)
                         :title "Edit Guardian"
                         :size "lg"}
            (when editing-guardian
              ($ lookup-inline-edit
                {:resource "guardians"
                 :record editing-guardian
                 :on-saved (fn [] (set-editing-guardian! nil) (refresh-links!))
                 :on-cancel #(set-editing-guardian! nil)
                 :form-field-component form-field})))

          ($ linked-guardians-card
            ;; Nothing to hide when there is no payer — every link belongs in
            ;; the table then, which is where you set one.
            {:exclude-ids (when payer #{(str (:id payer))})
             :initial-edit-id editing-link
             ;; The form saves through re-frame, into state this panel does not
             ;; read — so a saved change (unchecking Primary Payer, say) left the
             ;; card above showing the old payer until a manual refresh. Closing
             ;; the drawer is the moment to re-read; a cancel re-reads too, which
             ;; costs one request and cannot be wrong.
             :on-slide-over-change (fn [id]
                                     (set-editing-link! id)
                                     (when (nil? id) (refresh-links!)))
             :parent-agg-key parent-agg-key
             :parent-id parent-id
             :child-agg-key child-agg-key
             :fk-field fk-field
             :junction junction
             :add add
             :empty-message empty-message
             :parent-record parent-record
             :payer-name payer-name
             :on-changed refresh-links!})
          ($ discounts-card {:discounts (logic/discounts-for discounts parent-id)
                             :payer-name payer-name
                             :student-name student-name
                             :payer-guardian-id payer-guardian-id
                             :student-id parent-id
                             :on-created refresh-discounts!}))))))
